import { useCallback, useState } from 'react'
import type { LeadFields, SubmitState } from '../types/landing'

const EMPTY: LeadFields = { name: '', email: '', phone: '', zip: '', area: '' }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface UseLeadFormOptions {
  require?: Array<keyof LeadFields>
  initial?: Partial<LeadFields>
}

export function useLeadForm({ require = ['email'], initial }: UseLeadFormOptions = {}) {
  const [fields, setFields] = useState<LeadFields>({ ...EMPTY, ...initial })
  const [errors, setErrors] = useState<Partial<Record<keyof LeadFields, string>>>({})
  const [status, setStatus] = useState<SubmitState>('idle')

  const setField = useCallback((key: keyof LeadFields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev))
  }, [])

  const validate = useCallback((): boolean => {
    const next: Partial<Record<keyof LeadFields, string>> = {}
    require.forEach((key) => {
      const value = fields[key].trim()
      if (!value) {
        next[key] = 'Required'
        return
      }
      if (key === 'email' && !EMAIL_RE.test(value)) next.email = 'Enter a valid email address'
      if (key === 'zip' && !/^\d{5}$/.test(value)) next.zip = 'Enter a 5-digit zip code'
      if (key === 'phone' && value.replace(/\D/g, '').length < 10) next.phone = 'Enter a 10-digit phone number'
    })
    setErrors(next)
    return Object.keys(next).length === 0
  }, [fields, require])

  const submit = useCallback(
    async (onDone?: (values: LeadFields) => void) => {
      if (!validate()) {
        setStatus('error')
        return false
      }
      setStatus('loading')
      try {
        await new Promise((resolve) => setTimeout(resolve, 900))
        setStatus('success')
        onDone?.(fields)
        return true
      } catch {
        setStatus('error')
        return false
      }
    },
    [fields, validate],
  )

  const reset = useCallback(() => {
    setFields({ ...EMPTY, ...initial })
    setErrors({})
    setStatus('idle')
  }, [initial])

  return { fields, errors, status, setField, submit, reset }
}
